<template>
  <!-- The app's Create personal access token dialog (lib/view/mcp/widgets/
       mcp_personal_tokens_section.dart: aiConnectionsCreateTokenTitle, …Subtitle,
       aiConnectionsPermissions, aiConnectionsSelectedCount, aiConnectionsExpiry, the expiry
       presets 30 / 90 (default) / 365 days / none, cancel, aiConnectionsCreateToken; then
       aiConnectionsTokenCreated, aiConnectionsTokenShownOnce, aiConnectionsCopyToken). Scope chips
       are the first twelve of kMcpScopeOptions, in its order, with workspace:read on by default.
       The token name is sample data. Drawn in the app's dark theme on the dark band; plays once in
       view, then rests with a Replay button; resting styles are the final frame. -->
  <div ref="rootEl" class="stk">
    <div :key="runKey" class="stk-stage" :class="{ 'is-run': running || armed, 'is-armed': armed }" aria-hidden="true" translate="no">
    <div class="stk-dialog">
      <b class="stk-title">Create personal access token</b>
      <p class="stk-sub">Scoped to your current workspace. The token is shown once.</p>
      <span class="stk-input">Invoice sync</span>

      <p class="stk-label"><span>Permissions</span><span>{{ selected }} selected</span></p>
      <span class="stk-chips">
        <span v-for="scope in scopes" :key="scope.name" class="stk-chip stk-mono" :class="{ 'is-sel': scope.at !== undefined }" :style="scope.at ? { '--s': `${scope.at}s` } : null">
          <span class="stk-chip-off">{{ scope.name }}</span>
          <span v-if="scope.at !== undefined" class="stk-chip-on">{{ scope.name }}</span>
        </span>
      </span>

      <p class="stk-label"><span>Expiry</span></p>
      <span class="stk-chips">
        <span v-for="option in expiry" :key="option" class="stk-chip" :class="{ 'is-sel is-static': option === '90 days' }">
          <span class="stk-chip-off">{{ option }}</span>
          <span v-if="option === '90 days'" class="stk-chip-on">{{ option }}</span>
        </span>
      </span>

      <div class="stk-buttons">
        <span class="stk-btn-quiet">Cancel</span>
        <span class="stk-btn">Create token</span>
      </div>
    </div>

    <div class="stk-created">
      <b>Token created</b>
      <p>Copy this now. You won't be able to see it again.</p>
      <span class="stk-token stk-mono">mcp_pat_••••••••••••••••</span>
      <span class="stk-copy">Copy token</span>
    </div>
    </div>
    <StoryReplayButton v-show="canReplay" @click="replay" />
  </div>
</template>

<script setup>
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });
// The run ends when the created card has risen (2.6 s + 0.6 s); Replay shows after it.
const { runKey, canReplay, replay } = useStoryReplay(running, 3300);

// `at` (s) is when a chip switches on; 0 is on from the start (workspace:read, the default).
const scopes = [
  { name: "workspace:read", at: 0 },
  { name: "clients:read", at: 0.7 },
  { name: "clients:write" },
  { name: "invoices:read", at: 1 },
  { name: "invoices:write", at: 1.3 },
  { name: "proposals:read" },
  { name: "proposals:write" },
  { name: "contracts:read" },
  { name: "contracts:write" },
  { name: "contracts:send" },
  { name: "projects:read" },
  { name: "projects:write" },
];
const selected = scopes.filter((scope) => scope.at !== undefined).length;
const expiry = ["30 days", "90 days", "1 year", "No expiry"];
</script>

<style scoped>
.stk {
  position: relative;
  border-radius: 14px;
  background:
    radial-gradient(circle, rgb(255 255 255 / 0.1) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(120% 120% at 50% 40%, rgb(27 167 206 / 0.18), rgb(27 167 206 / 0) 70%);
  padding: 2rem 2rem 3rem;
}
.stk-stage { display: grid; }
.stk-dialog {
  display: grid;
  gap: 0.5rem;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  border: 1px solid rgb(255 255 255 / 0.13);
  border-radius: 10px;
  background: #1c1c1c;
  padding: 1rem;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  box-shadow: 0 24px 60px -20px rgb(0 0 0 / 0.8);
}
.stk-title { font-size: 15px; font-weight: 700; }
.stk-sub { margin: -0.3rem 0 0.2rem; color: #a8a8a8; font-size: 12px; }
.stk-input { border: 1px solid rgb(255 255 255 / 0.15); border-radius: 7px; padding: 0.55rem 0.7rem; }
.stk-label { display: flex; justify-content: space-between; margin: 0.45rem 0 0; color: #a8a8a8; font-size: 12px; font-weight: 600; }
.stk-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.stk-chip { display: inline-grid; }
.stk-chip > span { grid-area: 1 / 1; border: 1px solid rgb(255 255 255 / 0.15); border-radius: 5px; padding: 0.15rem 0.45rem; font-size: 11.5px; white-space: nowrap; }
.stk-mono > span { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 11px; }
.stk-chip-on { border-color: #ececec !important; background: #ececec; color: #171717; }
.stk-chip.is-sel .stk-chip-off { opacity: 0; }
.stk-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin-top: 0.6rem; }
.stk-btn-quiet, .stk-btn { position: relative; display: grid; place-items: center; min-height: 36px; border-radius: 7px; font-weight: 600; }
.stk-btn-quiet { border: 1px solid rgb(255 255 255 / 0.15); }
.stk-btn { background: #1ba7ce; color: #04151c; }

/* The created state, laid over the dialog's foot as the app's result step replaces it. */
.stk-created {
  justify-self: center;
  display: grid;
  gap: 0.4rem;
  width: min(100% - 1rem, 444px);
  margin-top: -4.4rem;
  border: 1px solid rgb(103 232 249 / 0.35);
  border-radius: 10px;
  background: #262626;
  padding: 0.85rem 0.95rem;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  box-shadow: 0 24px 60px -16px rgb(0 0 0 / 0.85);
}
.stk-created b { font-weight: 700; }
.stk-created p { margin: 0; color: #cfcfcf; font-size: 12px; }
.stk-token { border-radius: 6px; background: #111; padding: 0.4rem 0.55rem; color: #a7f3d0; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12px; overflow-wrap: anywhere; }
.stk-copy { justify-self: start; border-radius: 7px; background: #ececec; padding: 0.3rem 0.7rem; color: #171717; font-weight: 600; }

/* Motion: three scopes switch on, Create token is pressed, then the token appears once. */
.is-run .stk-chip.is-sel:not(.is-static) .stk-chip-off { animation: stk-off 0.25s ease var(--s) both; }
.is-run .stk-chip.is-sel:not(.is-static) .stk-chip-on { animation: stk-on 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) var(--s) both; }
.is-run .stk-btn { animation: stk-press 0.35s ease 2.1s both; }
.is-run .stk-created { animation: stk-rise 0.6s cubic-bezier(0.33, 1, 0.68, 1) 2.6s both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes stk-off { from { opacity: 1; } to { opacity: 0; } }
@keyframes stk-on { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: none; } }
@keyframes stk-press { 0% { transform: none; } 45% { transform: scale(0.95); } 100% { transform: none; } }
@keyframes stk-rise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }

@media (max-width: 639px) {
  .stk { padding: 0.9rem 0.9rem 2.9rem; }
  .stk-dialog { padding: 0.8rem; }
  .stk-created { width: auto; margin: 0.75rem 0 0; }
}
@media (prefers-reduced-motion: reduce) { .stk-stage * { animation: none !important; } }
</style>
